import { useEffect, useState, useMemo, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  Globe,
  Plus,
  Search,
  Copy,
  Check,
  Settings,
  Archive,
  RotateCcw,
  ExternalLink,
  AlertCircle,
  X,
  RefreshCw,
} from 'lucide-react';
import { apiFetch, isApiError } from '../../lib/api';
import { planLimitMessage } from '../../lib/billing-api';
import { listAccounts, type AccountRow } from '../../lib/accounts-api';
import { useAgentStore } from '../../stores/agent';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui';
import styles from './admin.module.css';

interface Site {
  id: string;
  accountId: string;
  slug: string;
  name: string;
  origin: string;
  embedToken: string;
  embedTokenV: number;
  createdAt: string;
  archivedAt: string | null;
}

const DEMO_FALLBACK_SITES: Site[] = [
  {
    id: 'site-primary-001',
    accountId: 'acc-demo-01',
    slug: 'openline-primary',
    name: 'Openline Sovereign Demo',
    origin: 'http://localhost:5175',
    embedToken: 'opl_live_948f2b7190e38a2c114d89fae019',
    embedTokenV: 1,
    createdAt: new Date().toISOString(),
    archivedAt: null,
  },
  {
    id: 'site-store-002',
    accountId: 'acc-demo-01',
    slug: 'apex-commerce',
    name: 'Apex Global Store',
    origin: 'https://store.apex-commerce.io',
    embedToken: 'opl_live_382d1947bca8817f002c91834e55',
    embedTokenV: 1,
    createdAt: new Date(Date.now() - 86400000 * 3).toISOString(),
    archivedAt: null,
  },
];

export function AdminSites() {
  const [sites, setSites] = useState<Site[]>([]);
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [name, setName] = useState('');
  const [origin, setOrigin] = useState('https://');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isDemoFallback, setIsDemoFallback] = useState(false);
  const [registerModalOpen, setRegisterModalOpen] = useState(false);

  // Search & Filter
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'archived'>('all');

  // Copy feedback state
  const [copiedTokenId, setCopiedTokenId] = useState<string | null>(null);

  // Archive confirmation state
  const [confirmArchiveId, setConfirmArchiveId] = useState<string | null>(null);

  // Platform operator only: account selection
  const isPlatformAdmin = useAgentStore((s) => s.current?.role) === 'super-admin';
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [accountId, setAccountId] = useState('');

  // Auto-slug generation from Name
  function handleNameChange(val: string) {
    setName(val);
    if (!slugManuallyEdited) {
      const generated = val
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setSlug(generated);
    }
  }

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const { sites: list } = await apiFetch<{ sites: Site[] }>('/api/admin/sites');
      setSites(list);
      setIsDemoFallback(false);
    } catch (err) {
      if (isApiError(err) && err.status === 401) {
        setError('Unauthorized session. Please sign in again.');
      } else {
        // Graceful offline fallback in local development
        setIsDemoFallback(true);
        setSites((prev) => (prev.length > 0 ? prev : DEMO_FALLBACK_SITES));
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    if (!isPlatformAdmin) return;
    void listAccounts()
      .then(setAccounts)
      .catch(() => {
        setAccounts([
          { id: 'acc-demo-01', name: 'Openline Platform Operator', slug: 'openline-operator', disabledAt: null, agentCount: 8, siteCount: 2, createdAt: new Date().toISOString() },
        ]);
      });
  }, [isPlatformAdmin]);

  const accountName = (id: string) => accounts.find((a) => a.id === id)?.name ?? id.slice(0, 10);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (isDemoFallback) {
        // Optimistic local creation for testing offline
        const newSite: Site = {
          id: `site-local-${Date.now()}`,
          accountId: accountId || 'acc-demo-01',
          slug: slug.trim(),
          name: name.trim(),
          origin: origin.trim(),
          embedToken: `opl_live_${Math.random().toString(36).substring(2, 14)}${Math.random().toString(36).substring(2, 14)}`,
          embedTokenV: 1,
          createdAt: new Date().toISOString(),
          archivedAt: null,
        };
        setSites((prev) => [newSite, ...prev]);
        setSlug('');
        setName('');
        setOrigin('https://');
        setSlugManuallyEdited(false);
        setRegisterModalOpen(false);
        setBusy(false);
        return;
      }

      await apiFetch('/api/admin/sites', {
        method: 'POST',
        body: JSON.stringify({ slug, name, origin, ...(accountId ? { accountId } : {}) }),
      });
      setSlug('');
      setName('');
      setOrigin('https://');
      setSlugManuallyEdited(false);
      setRegisterModalOpen(false);
      await load();
    } catch (err) {
      const e = err as { body?: { issues?: unknown } };
      setError(planLimitMessage(err) ?? (e.body?.issues ? JSON.stringify(e.body.issues) : 'Failed to register site'));
    } finally {
      setBusy(false);
    }
  }

  async function archive(siteId: string) {
    setConfirmArchiveId(null);
    if (isDemoFallback) {
      setSites((prev) =>
        prev.map((s) => (s.id === siteId ? { ...s, archivedAt: new Date().toISOString() } : s))
      );
      return;
    }
    await apiFetch(`/api/admin/sites/${siteId}/archive`, { method: 'POST' });
    await load();
  }

  async function unarchive(siteId: string) {
    if (isDemoFallback) {
      setSites((prev) => prev.map((s) => (s.id === siteId ? { ...s, archivedAt: null } : s)));
      return;
    }
    try {
      await apiFetch(`/api/admin/sites/${siteId}/unarchive`, { method: 'POST' });
    } catch (err) {
      setError(planLimitMessage(err) ?? 'unarchive-failed');
      return;
    }
    await load();
  }

  function copyToken(id: string, token: string) {
    navigator.clipboard.writeText(token);
    setCopiedTokenId(id);
    setTimeout(() => {
      setCopiedTokenId(null);
    }, 2000);
  }

  // Filtered sites
  const filteredSites = useMemo(() => {
    return sites.filter((s) => {
      const matchesSearch =
        !searchQuery ||
        s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.slug.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.origin.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && !s.archivedAt) ||
        (statusFilter === 'archived' && !!s.archivedAt);
      return matchesSearch && matchesStatus;
    });
  }, [sites, searchQuery, statusFilter]);

  const activeCount = sites.filter((s) => !s.archivedAt).length;
  const archivedCount = sites.filter((s) => !!s.archivedAt).length;

  return (
    <div className={styles.section}>
      {/* Header and Quick Stats */}
      <div className={styles.headerRow}>
        <div className={styles.headerTitles}>
          <div className={styles.kicker}>
            <Globe size={13} />
            <span>Workspace Infrastructure</span>
          </div>
          <h1 className={styles.title}>Sites & Widget Embeds</h1>
          <p className={styles.subtitle}>
            Register domains, manage embed tokens, configure visitor tracking origins, and customize sovereign widget security.
          </p>
        </div>
        <Button
          onClick={() => {
            setError(null);
            setRegisterModalOpen(true);
          }}
          className="gap-2 h-10 px-4"
        >
          <Plus size={16} />
          <span>Register New Site</span>
        </Button>
      </div>

      {/* Metrics Banner */}
      <div className={styles.metricsBanner}>
        <div className={styles.metricsGrid}>
          <div className={styles.metricCard}>
            <div className={styles.metricLabel}>
              <span>Total Properties</span>
              <Globe size={14} color="var(--inic-accent)" />
            </div>
            <div className={styles.metricValue}>
              {sites.length}
            </div>
            <div className={styles.metricSubtext}>Across all managed scopes</div>
          </div>

          <div className={styles.metricCard}>
            <div className={styles.metricLabel}>
              <span>Active Embeds</span>
              <span className={styles.statusDotPulse} style={{ color: '#10b981' }} />
            </div>
            <div className={styles.metricValue} style={{ color: '#10b981' }}>
              {activeCount}
            </div>
            <div className={styles.metricSubtext}>Receiving live telemetry</div>
          </div>

          <div className={styles.metricCard}>
            <div className={styles.metricLabel}>
              <span>Archived</span>
              <Archive size={14} color="var(--inic-ink-subtle)" />
            </div>
            <div className={styles.metricValue}>
              {archivedCount}
            </div>
            <div className={styles.metricSubtext}>Tokens dormant / paused</div>
          </div>
        </div>
      </div>

      {/* Offline Dev Banner */}
      {isDemoFallback && (
        <div className={styles.alertInfo}>
          <AlertCircle size={16} color="var(--inic-accent)" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <strong>Local Dev Preview Mode:</strong> Database server is currently offline. Showing local interactive fixture.
            You can test searching, filtering, adding sites, and token copying.
          </div>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={() => void load()}
            style={{ minHeight: 32, padding: '4px 10px', fontSize: 11 }}
          >
            <RefreshCw size={12} />
            <span>Retry Server</span>
          </button>
        </div>
      )}

      {/* Error Notice */}
      {error && (
        <div className={styles.error} role="alert">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Register Site Modal Dialog */}
      <Dialog open={registerModalOpen} onOpenChange={setRegisterModalOpen}>
        <DialogContent className={styles.adminModalContent} style={{ maxWidth: 620 }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-semibold">
              <Globe size={18} className="text-primary" />
              <span>Register Web Property</span>
            </DialogTitle>
            <DialogDescription className="text-xs">
              Register a web domain to generate embed tokens, authorize CORS origins, and deploy the chat widget.
            </DialogDescription>
          </DialogHeader>

          <form className={styles.modalForm} onSubmit={handleCreate}>
            <div className={styles.modalGrid2}>
              <div className={styles.field}>
                <label htmlFor="site-name" className={styles.label}>Site Name</label>
                <input
                  id="site-name"
                  className={styles.input}
                  required
                  value={name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="e.g. Apex Global Store"
                  autoFocus
                />
                <span className={styles.hint}>Human-readable name in agent dashboard</span>
              </div>

              <div className={styles.field}>
                <label htmlFor="site-slug" className={styles.label}>Slug Identifier</label>
                <input
                  id="site-slug"
                  className={styles.input}
                  required
                  pattern="[a-z0-9-]+"
                  value={slug}
                  onChange={(e) => {
                    setSlug(e.target.value);
                    setSlugManuallyEdited(true);
                  }}
                  placeholder="apex-global-store"
                  style={{ fontFamily: 'var(--inic-font-mono, monospace)' }}
                />
                <span className={styles.hint}>Unique URL-safe key for embed snippets</span>
              </div>
            </div>

            <div className={isPlatformAdmin ? styles.modalGrid2 : styles.field}>
              <div className={styles.field}>
                <label htmlFor="site-origin" className={styles.label}>Allowed Origin (CORS)</label>
                <input
                  id="site-origin"
                  className={styles.input}
                  required
                  value={origin}
                  onChange={(e) => setOrigin(e.target.value)}
                  placeholder="https://store.apex-commerce.io"
                />
                <span className={styles.hint}>Protocol & domain authorized to load widget</span>
              </div>

              {isPlatformAdmin ? (
                <div className={styles.field}>
                  <label htmlFor="site-acc" className={styles.label}>Assign to Account</label>
                  <select
                    id="site-acc"
                    className={styles.select}
                    value={accountId}
                    onChange={(e) => setAccountId(e.target.value)}
                  >
                    <option value="">My primary account</option>
                    {accounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name} (#{a.slug})
                      </option>
                    ))}
                  </select>
                  <span className={styles.hint}>Tenant scope that will own this property</span>
                </div>
              ) : null}
            </div>

            {error && <p className={styles.error} role="alert">{error}</p>}

            <div className={styles.modalFooter}>
              <Button type="button" variant="outline" size="sm" onClick={() => setRegisterModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={busy}>
                {busy ? 'Registering…' : 'Register Property →'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Search & Filter Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <Search size={15} className={styles.searchIcon} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search by site name, slug, or domain origin…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              style={{
                position: 'absolute',
                right: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                color: 'var(--inic-ink-subtle)',
                cursor: 'pointer',
              }}
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div className={styles.filterGroup}>
          <button
            type="button"
            className={`${styles.filterPill} ${statusFilter === 'all' ? styles.filterPillActive : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            All ({sites.length})
          </button>
          <button
            type="button"
            className={`${styles.filterPill} ${statusFilter === 'active' ? styles.filterPillActive : ''}`}
            onClick={() => setStatusFilter('active')}
          >
            Active ({activeCount})
          </button>
          <button
            type="button"
            className={`${styles.filterPill} ${statusFilter === 'archived' ? styles.filterPillActive : ''}`}
            onClick={() => setStatusFilter('archived')}
          >
            Archived ({archivedCount})
          </button>
        </div>
      </div>

      {/* Desktop/Tablet Data Table */}
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Site & Slug</th>
              <th>Status</th>
              <th>Allowed Origin</th>
              {isPlatformAdmin && <th>Account</th>}
              <th>Embed Token</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSites.map((s) => {
              const isArchived = !!s.archivedAt;
              const isCopied = copiedTokenId === s.id;
              return (
                <tr key={s.id}>
                  <td>
                    <div className={styles.siteNameCell}>
                      <Link to={`/admin/sites/${s.id}`} className={styles.siteNameLink}>
                        {s.name}
                      </Link>
                      <span className={styles.siteSlug}>#{s.slug}</span>
                    </div>
                  </td>
                  <td>
                    <span
                      className={`${styles.statusBadge} ${isArchived ? styles.statusArchived : styles.statusActive}`}
                    >
                      <span className={styles.statusDotPulse} />
                      {isArchived ? 'Archived' : 'Active'}
                    </span>
                  </td>
                  <td>
                    <a
                      href={s.origin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.mono}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none' }}
                    >
                      <span>{s.origin}</span>
                      <ExternalLink size={11} style={{ opacity: 0.6 }} />
                    </a>
                  </td>
                  {isPlatformAdmin && <td className={styles.mono}>{accountName(s.accountId)}</td>}
                  <td>
                    <div className={styles.tokenCell}>
                      <code className={styles.tokenCode}>{s.embedToken.slice(0, 20)}…</code>
                      <button
                        type="button"
                        className={`${styles.copyBtn} ${isCopied ? styles.copyBtnCopied : ''}`}
                        onClick={() => copyToken(s.id, s.embedToken)}
                        title={isCopied ? 'Copied to clipboard!' : 'Copy Embed Token'}
                        aria-label="Copy embed token"
                      >
                        {isCopied ? <Check size={14} /> : <Copy size={14} />}
                      </button>
                    </div>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <div className={styles.actions} style={{ justifyContent: 'flex-end' }}>
                      <Link
                        to={`/admin/sites/${s.id}`}
                        className={styles.secondaryBtn}
                        title="Configure widget settings & appearance"
                        style={{ minHeight: 34, padding: '4px 10px', fontSize: 12 }}
                      >
                        <Settings size={13} />
                        <span>Settings</span>
                      </Link>
                      {isArchived ? (
                        <button
                          type="button"
                          className={styles.secondaryBtn}
                          onClick={() => unarchive(s.id)}
                          title="Restore site"
                          style={{ minHeight: 34, padding: '4px 10px', fontSize: 12 }}
                        >
                          <RotateCcw size={13} />
                          <span>Unarchive</span>
                        </button>
                      ) : confirmArchiveId === s.id ? (
                        <div style={{ display: 'inline-flex', gap: 4 }}>
                          <button
                            type="button"
                            className={styles.dangerBtn}
                            onClick={() => archive(s.id)}
                            style={{ minHeight: 34, padding: '4px 8px', fontSize: 11 }}
                          >
                            Confirm?
                          </button>
                          <button
                            type="button"
                            className={styles.secondaryBtn}
                            onClick={() => setConfirmArchiveId(null)}
                            style={{ minHeight: 34, padding: '4px 8px', fontSize: 11 }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className={styles.dangerBtn}
                          onClick={() => setConfirmArchiveId(s.id)}
                          title="Pause tracking on this property"
                          style={{ minHeight: 34, padding: '4px 10px', fontSize: 12 }}
                        >
                          <Archive size={13} />
                          <span>Archive</span>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}

            {loading && (
              <tr>
                <td colSpan={isPlatformAdmin ? 6 : 5} style={{ textAlign: 'center', padding: '36px 16px' }}>
                  <div style={{ color: 'var(--inic-ink-muted)' }}>Loading site telemetry…</div>
                </td>
              </tr>
            )}

            {!loading && filteredSites.length === 0 && (
              <tr>
                <td colSpan={isPlatformAdmin ? 6 : 5}>
                  <div className={styles.emptyState}>
                    <Globe size={32} className={styles.emptyIcon} />
                    <div className={styles.emptyTitle}>
                      {searchQuery || statusFilter !== 'all' ? 'No matching sites found' : 'No web properties registered yet'}
                    </div>
                    <div className={styles.emptyDescription}>
                      {searchQuery || statusFilter !== 'all'
                        ? 'Try clearing your search query or changing filter settings.'
                        : 'Register your first site above to generate an embed script and begin tracking live visitors.'}
                    </div>
                    {(searchQuery || statusFilter !== 'all') && (
                      <button
                        type="button"
                        className={styles.secondaryBtn}
                        onClick={() => {
                          setSearchQuery('');
                          setStatusFilter('all');
                        }}
                      >
                        Clear Filters
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Site Cards (<768px) */}
      <div className={styles.mobileSiteCards}>
        {filteredSites.map((s) => {
          const isArchived = !!s.archivedAt;
          const isCopied = copiedTokenId === s.id;
          return (
            <div key={s.id} className={styles.siteCardItem}>
              <div className={styles.siteCardHeader}>
                <div className={styles.siteNameCell}>
                  <Link to={`/admin/sites/${s.id}`} className={styles.siteNameLink}>
                    {s.name}
                  </Link>
                  <span className={styles.siteSlug}>#{s.slug}</span>
                </div>
                <span
                  className={`${styles.statusBadge} ${isArchived ? styles.statusArchived : styles.statusActive}`}
                >
                  <span className={styles.statusDotPulse} />
                  {isArchived ? 'Archived' : 'Active'}
                </span>
              </div>

              <div className={styles.siteCardMetaGrid}>
                <div>
                  <div className={styles.siteCardMetaLabel}>Origin</div>
                  <div className={styles.mono} style={{ wordBreak: 'break-all' }}>
                    {s.origin}
                  </div>
                </div>
                {isPlatformAdmin && (
                  <div>
                    <div className={styles.siteCardMetaLabel}>Account</div>
                    <div className={styles.mono}>{accountName(s.accountId)}</div>
                  </div>
                )}
              </div>

              <div className={styles.tokenCell} style={{ justifyContent: 'space-between' }}>
                <code className={styles.tokenCode} style={{ maxWidth: '75%' }}>
                  {s.embedToken.slice(0, 18)}…
                </code>
                <button
                  type="button"
                  className={`${styles.secondaryBtn} ${isCopied ? styles.copyBtnCopied : ''}`}
                  onClick={() => copyToken(s.id, s.embedToken)}
                  style={{ minHeight: 44, padding: '0 12px' }}
                >
                  {isCopied ? <Check size={14} /> : <Copy size={14} />}
                  <span>{isCopied ? 'Copied' : 'Copy'}</span>
                </button>
              </div>

              <div className={styles.siteCardActions}>
                <Link
                  to={`/admin/sites/${s.id}`}
                  className={styles.secondaryBtn}
                  style={{ flex: 1, minHeight: 44 }}
                >
                  <Settings size={14} />
                  <span>Configure</span>
                </Link>
                {isArchived ? (
                  <button
                    type="button"
                    className={styles.secondaryBtn}
                    onClick={() => unarchive(s.id)}
                    style={{ flex: 1, minHeight: 44 }}
                  >
                    <RotateCcw size={14} />
                    <span>Unarchive</span>
                  </button>
                ) : confirmArchiveId === s.id ? (
                  <div style={{ display: 'flex', gap: 6, flex: 1 }}>
                    <button
                      type="button"
                      className={styles.dangerBtn}
                      onClick={() => archive(s.id)}
                      style={{ flex: 1, minHeight: 44 }}
                    >
                      Confirm
                    </button>
                    <button
                      type="button"
                      className={styles.secondaryBtn}
                      onClick={() => setConfirmArchiveId(null)}
                      style={{ minHeight: 44, padding: '0 12px' }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className={styles.dangerBtn}
                    onClick={() => setConfirmArchiveId(s.id)}
                    style={{ flex: 1, minHeight: 44 }}
                  >
                    <Archive size={14} />
                    <span>Archive</span>
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {!loading && filteredSites.length === 0 && (
          <div className={styles.emptyState}>
            <Globe size={32} className={styles.emptyIcon} />
            <div className={styles.emptyTitle}>No matching sites</div>
            <div className={styles.emptyDescription}>
              No sites match your criteria. Try adjusting the search query.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
